import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listEvents } from '../services/eventsService';
import { listVendors } from '../services/vendorsService';
import { getUserProfile } from '../services/profilesService';
import { collection, getDocs, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../sdk_initialization';
import './ProfilePage.css';

const TransactionsAdmin = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState('');
  const [eventChoices, setEventChoices] = useState([]); // {eventId,eventName}
  const [eventId, setEventId] = useState('');
  const [eventName, setEventName] = useState('');

  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [vendorChoices, setVendorChoices] = useState([]); // {vendorId,vendorName,vendorNumber}
  const [vendorId, setVendorId] = useState('All');
  const [vendorName, setVendorName] = useState('All Vendors');
  const [vendorNumber, setVendorNumber] = useState('All'); // Track vendorNumber for filtering

  const [searchText, setSearchText] = useState(''); // Text search for vendor/contact name/email
  const [statusFilter, setStatusFilter] = useState('All'); // All | Succeeded | Refunded | Failed | Incomplete | Partially Refunded
  const [purchaseFilter, setPurchaseFilter] = useState('All'); // All | Booths | Attendees | Manual
  const [onlyDuplicates, setOnlyDuplicates] = useState(false);
  const [hideFailedTransactions, setHideFailedTransactions] = useState(false);
  const [hideIncompleteTransactions, setHideIncompleteTransactions] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Used to trigger re-fetch

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  const STORAGE_KEY = 'transactionsAdmin.filters';

  // Load persisted filters from sessionStorage on first mount
  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      const allowedStatusFilters = new Set(['All', 'Succeeded', 'Refunded', 'Partially Refunded', 'Failed', 'Incomplete', 'Other']);
      if (saved) {
        if (saved.eventId) setEventId(saved.eventId);
        if (saved.eventName) setEventName(saved.eventName);
        if (saved.vendorId) setVendorId(saved.vendorId);
        if (saved.vendorName) setVendorName(saved.vendorName);
        if (saved.vendorNumber) setVendorNumber(saved.vendorNumber);
        if (saved.searchText) setSearchText(saved.searchText);
        if (saved.statusFilter && allowedStatusFilters.has(saved.statusFilter)) setStatusFilter(saved.statusFilter);
        if (saved.purchaseFilter) setPurchaseFilter(saved.purchaseFilter);
        if (typeof saved.onlyDuplicates === 'boolean') setOnlyDuplicates(saved.onlyDuplicates);
        if (typeof saved.hideFailedTransactions === 'boolean') setHideFailedTransactions(saved.hideFailedTransactions);
        if (typeof saved.hideIncompleteTransactions === 'boolean') setHideIncompleteTransactions(saved.hideIncompleteTransactions);
        if (saved.currentPage) setCurrentPage(saved.currentPage);
        if (saved.sortBy) setSortBy(saved.sortBy);
        if (saved.sortDir) setSortDir(saved.sortDir);
      }
    } catch (e) {
      // ignore JSON errors
    }
  }, []);

  // Persist filters to sessionStorage whenever they change
  useEffect(() => {
    try {
      const payload = {
        eventId,
        eventName,
        vendorId,
        vendorName,
        vendorNumber,
        searchText,
        statusFilter,
        purchaseFilter,
        onlyDuplicates,
        hideFailedTransactions,
        hideIncompleteTransactions,
        currentPage,
        sortBy,
        sortDir
      };
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      // ignore storage errors
    }
  }, [eventId, eventName, vendorId, vendorName, vendorNumber, searchText, statusFilter, purchaseFilter, onlyDuplicates, hideFailedTransactions, hideIncompleteTransactions, currentPage, sortBy, sortDir]);

  // Cache for profile data
  const profileCacheRef = React.useRef(new Map());

  // Load current user profile to check if admin
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!currentUser?.uid) return;
      try {
        const profile = await getUserProfile(currentUser.uid);
        if (!cancelled) {
          setUserProfile(profile);
          const role = String(profile?.userType || '').trim();
          const adminUser = role === 'Admin' || role === 'SystemAdmin' || role === 'Organizer' || role === 'EventAdmin';
          setIsAdmin(adminUser);
        }
      } catch (e) {
        console.error('Failed to load user profile:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  // Load events (only open and closed events)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setEventsLoading(true);
        setEventsError('');
        const all = await listEvents();
        // Filter to only show Open and Closed events
        const active = (all || []).filter(ev => {
          const status = (ev?.status || '').toLowerCase();
          return status === 'open' || status === 'closed';
        });
        // Restrict visible events based on role:
        // - Admin: see full active list
        // - EventAdmin: see events where their UID is in `eventAdmins`
        // - Organizer: see events where their UID is in `organizers`
        // - Vendor: events where vendor has transactions OR reserved booths
        // - Others: see none
        let filtered = active;
        try {
          if (currentUser && currentUser.uid) {
            try {
              const prof = await getUserProfile(currentUser.uid);
              const uidLower = String(currentUser.uid || '').toLowerCase();
              if (prof?.userType === 'EventAdmin') {
                filtered = (active || []).filter(ev => Array.isArray(ev.eventAdmins) && ev.eventAdmins.some(a => String(a || '').toLowerCase() === uidLower));
              } else if (prof?.userType === 'Organizer') {
                filtered = (active || []).filter(ev => Array.isArray(ev.organizers) && ev.organizers.some(a => String(a || '').toLowerCase() === uidLower));
              } else if (prof?.userType === 'Vendor') {
                const vendorIdForUser = String(prof?.vendorId || '').trim();
                const allowedEventIds = new Set();

                // Include events where this vendor has reserved booths.
                if (vendorIdForUser) {
                  try {
                    const reservationsQ = query(
                      collection(db, 'boothReservations'),
                      where('vendorId', '==', vendorIdForUser),
                      where('status', '==', 'Reserved')
                    );
                    const reservationsSnap = await getDocs(reservationsQ);
                    reservationsSnap.forEach(d => {
                      const eventIdFromReservation = d.data()?.eventId;
                      if (eventIdFromReservation) {
                        allowedEventIds.add(String(eventIdFromReservation));
                      }
                    });
                  } catch (e) {
                    // Keep loading from transactions if reservations lookup fails.
                  }

                }

                // Also include events where the current profile has transactions (legacy fallback).
                try {
                  const profileTxnsQ = query(
                    collection(db, 'transactions'),
                    where('profileId', '==', currentUser.uid)
                  );
                  const profileTxnsSnap = await getDocs(profileTxnsQ);
                  profileTxnsSnap.forEach(d => {
                    const eventIdFromTxn = d.data()?.eventId;
                    if (eventIdFromTxn) {
                      allowedEventIds.add(String(eventIdFromTxn));
                    }
                  });
                } catch (e) {
                  // Ignore profile fallback errors.
                }

                filtered = (active || []).filter(ev => allowedEventIds.has(String(ev.id)));
              } else if (prof?.userType !== 'Admin' && prof?.userType !== 'SystemAdmin') {
                filtered = [];
              }
            } catch (profileErr) {
              filtered = [];
            }
          }
        } catch (e) {
          filtered = [];
        }

        const entries = (filtered || []).map(ev => ({
          eventId: ev.id,
          eventName: ev.name || ev.id,
        }));
        entries.sort((a, b) => String(a.eventName).localeCompare(String(b.eventName)));
        if (!cancelled) {
          setEventChoices(entries);

          // If persisted eventId is no longer visible for this role/user,
          // clear it so no hidden stale selection can drive transaction queries.
          setEventId(prevEventId => {
            const currentEventId = String(prevEventId || '').trim();
            if (!currentEventId) return prevEventId;
            const isCurrentVisible = entries.some(e => e.eventId === currentEventId);
            if (isCurrentVisible) return prevEventId;

            setEventName('');
            // Clear admin vendor filters as well to avoid accidental broad queries
            // from stale session values when access context changed.
            setVendorId('All');
            setVendorName('All Vendors');
            setVendorNumber('All');
            return '';
          });
        }
      } catch (e) {
        if (!cancelled) setEventsError('Failed to load events.');
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.uid]);

  // Load vendors (only for admins)
  useEffect(() => {
    if (!isAdmin) return; // Skip loading vendors for non-admin users
    
    let cancelled = false;
    (async () => {
      try {
        setVendorsLoading(true);
        const all = await listVendors();
        const entries = (all || []).map(v => ({
          vendorId: v.id,
          vendorName: v.name || v.id,
          vendorNumber: v.vendorNumber || v.id, // Include vendorNumber for filtering
        }));
        entries.sort((a, b) => String(a.vendorName).localeCompare(String(b.vendorName)));
        if (!cancelled) {
          setVendorChoices(entries);
        }
      } catch (e) {
        if (!cancelled) console.error('Failed to load vendors:', e);
      } finally {
        if (!cancelled) setVendorsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  // Subscribe to transactions with real-time updates
  useEffect(() => {
    if (!currentUser?.uid) return;

    // Do not load any transactions until an event is explicitly selected.
    if (!eventId || eventId === 'All') {
      setTransactions([]);
      setLoading(false);
      setError('');
      return;
    }
    
    setLoading(true);
    setError('');

    const col = collection(db, 'transactions');
    let q;

    if (isAdmin) {
      // Admin users: Build query based on filters
      if (eventId !== 'All' && vendorNumber !== 'All') {
        // Filter by both event and vendor
        q = query(
          col,
          where('eventId', '==', eventId),
          where('metadata.vendorNumber', '==', vendorNumber),
          orderBy('createdAt', 'desc')
        );
      } else if (eventId !== 'All') {
        // Filter by event only
        q = query(
          col,
          where('eventId', '==', eventId),
          orderBy('createdAt', 'desc')
        );
      } else if (vendorNumber !== 'All') {
        // Filter by vendor only
        q = query(
          col,
          where('metadata.vendorNumber', '==', vendorNumber),
          orderBy('createdAt', 'desc')
        );
      } else {
        // No filters - get all transactions
        q = query(col, orderBy('createdAt', 'desc'));
      }
    } else {
      const userRole = String(userProfile?.userType || '').trim();
      const vendorIdForUser = String(userProfile?.vendorId || '').trim();

      // Vendor users: use profileId-based reads to comply with Firestore rules.
      if (userRole === 'Vendor' && eventId !== 'All') {
        q = query(
          col,
          where('profileId', '==', currentUser.uid),
          where('eventId', '==', eventId),
          orderBy('createdAt', 'desc')
        );
      } else if (eventId !== 'All') {
        // Other non-admin users: show their own transactions.
        q = query(
          col,
          where('profileId', '==', currentUser.uid),
          where('eventId', '==', eventId),
          orderBy('createdAt', 'desc')
        );
      } else {
        q = query(
          col,
          where('profileId', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        );
      }
    }

    const unsubscribe = onSnapshot(
      q,
      async (snap) => {
        const txnDocs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        
        // Enrich with profile data
        const profileCache = profileCacheRef.current;
        const neededProfileIds = new Set();
        txnDocs.forEach(txn => {
          if (txn.profileId && !profileCache.has(txn.profileId)) {
            neededProfileIds.add(txn.profileId);
          }
        });

        // Load missing profiles
        if (neededProfileIds.size > 0) {
          await Promise.all(
            Array.from(neededProfileIds).map(async pid => {
              try {
                const prof = await getUserProfile(pid);
                if (prof) profileCache.set(pid, prof);
              } catch (e) {
                console.error('Failed to load profile:', pid, e);
              }
            })
          );
        }

        // Enrich transactions with profile and vendor data
        const enriched = txnDocs.map(txn => {
          const profile = profileCache.get(txn.profileId);
          return {
            ...txn,
            contactName: profile ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim() : '',
            contactEmail: profile?.email || '',
            contactPhone: profile?.phone || '',
            // Use transaction metadata first; fallback to profile fields only.
            vendorNameFallback: profile?.businessName || '',
            vendorNumberFallback: profile?.vendorNumber || '',
          };
        });

        setTransactions(enriched);
        setLoading(false);
      },
      (err) => {
        console.error('Transaction subscription error:', err);
        setError(err?.message || 'Failed to load transactions');
        setLoading(false);
      }
    );

    return () => {
      try {
        if (typeof unsubscribe === 'function') unsubscribe();
      } catch (e) {
        console.error('Error unsubscribing:', e);
      }
    };
  }, [currentUser, isAdmin, userProfile, eventId, vendorNumber, refreshTrigger]);

  // Filter to show all transaction types, and apply status filter
  const filteredBase = useMemo(() => {
    return transactions.filter(txn => {
      const status = (txn.status || '').toLowerCase();
      const knownStatuses = new Set(['succeeded', 'refunded', 'failed', 'incomplete', 'partially_refunded']);
      
      // Apply status filter
      if (statusFilter === 'Succeeded' && status !== 'succeeded') return false;
      if (statusFilter === 'Refunded' && status !== 'refunded') return false;
      if (statusFilter === 'Failed' && status !== 'failed') return false;
      if (statusFilter === 'Incomplete' && status !== 'incomplete') return false;
      if (statusFilter === 'Partially Refunded' && status !== 'partially_refunded') return false;
      if (statusFilter === 'Other' && knownStatuses.has(status)) return false;
      
      // Apply text search filter (admin only) — include booth numbers
      if (isAdmin && searchText.trim()) {
        const search = searchText.toLowerCase().trim();
        const vendorName = (txn.metadata?.vendorName || txn.vendorNameFallback || '').toLowerCase();
        const contactName = (txn.contactName || '').toLowerCase();
        const contactEmail = (txn.contactEmail || '').toLowerCase();
        const payeeName = (txn.metadata?.payeeName || '').toLowerCase();

        // Check booths array (if present)
        let boothsText = '';
        try {
          if (Array.isArray(txn.booths) && txn.booths.length > 0) {
            boothsText = txn.booths.map(b => (b.number || b.id || b.boothNumber || b).toString()).join(' ').toLowerCase();
          } else if (Array.isArray(txn.metadata?.booths) && txn.metadata.booths.length > 0) {
            boothsText = txn.metadata.booths.map(b => (b.number || b.id || b.boothNumber || b).toString()).join(' ').toLowerCase();
          } else if (txn.metadata?.boothNumber) {
            boothsText = String(txn.metadata.boothNumber).toLowerCase();
          } else if (txn.metadata?.boothNumbers) {
            boothsText = String(txn.metadata.boothNumbers).toLowerCase();
          }
        } catch (e) {
          boothsText = '';
        }

        const matches = 
          vendorName.includes(search) ||
          contactName.includes(search) ||
          contactEmail.includes(search) ||
          payeeName.includes(search) ||
          (boothsText && boothsText.includes(search));

        if (!matches) return false;
      }

      // Apply purchase type filter
      if (purchaseFilter && purchaseFilter !== 'All') {
        const want = (purchaseFilter || '').toString().toLowerCase();
        // Detect actual purchase type on transaction
        let actual = '';
        try {
          if (Array.isArray(txn.booths) && txn.booths.length > 0) actual = 'booths';
          else if (Number(txn.metadata?.attendeeCount || txn.metadata?.attendee_count || txn.metadata?.attendees || 0) > 0) actual = 'attendees';
          else if (txn.metadata && typeof txn.metadata.purchaseType === 'string') actual = (txn.metadata.purchaseType || '').toLowerCase();
          else if (txn.metadata && (txn.metadata.manual === true || (txn.metadata.source || '').toLowerCase() === 'manual')) actual = 'manual';
        } catch (e) {
          actual = '';
        }
        if (want === 'booths' && actual !== 'booths') return false;
        if (want === 'attendees' && actual !== 'attendees') return false;
        if (want === 'manual' && actual !== 'manual') return false;
      }
      
      return true;
    });
  }, [transactions, statusFilter, searchText, isAdmin, purchaseFilter]);

  // Apply the hide-status checkboxes on top of the base filters.
  const visibleBase = useMemo(() => {
    return (filteredBase || []).filter(txn => {
      const status = (txn.status || '').toLowerCase();
      if (hideFailedTransactions && status === 'failed') return false;
      if (hideIncompleteTransactions && status === 'incomplete') return false;
      return true;
    });
  }, [filteredBase, hideFailedTransactions, hideIncompleteTransactions]);

  // Compute booth occurrence counts across the current filteredBase set
  const boothCounts = useMemo(() => {
    const counts = new Map();
    (filteredBase || []).forEach(txn => {
      try {
        if (Array.isArray(txn.booths) && txn.booths.length > 0) {
          txn.booths.forEach(b => {
            const n = String(b.number || b.id || b.boothNumber || b).trim();
            if (!n) return;
            counts.set(n, (counts.get(n) || 0) + 1);
          });
        } else if (Array.isArray(txn.metadata?.booths) && txn.metadata.booths.length > 0) {
          txn.metadata.booths.forEach(b => {
            const n = String(b.number || b.id || b.boothNumber || b).trim();
            if (!n) return;
            counts.set(n, (counts.get(n) || 0) + 1);
          });
        } else if (txn.metadata?.boothNumber) {
          const n = String(txn.metadata.boothNumber).trim();
          if (n) counts.set(n, (counts.get(n) || 0) + 1);
        } else if (txn.metadata?.boothNumbers) {
          const raw = String(txn.metadata.boothNumbers);
          raw.split(/[;,\s]+/).forEach(part => {
            const n = part.trim();
            if (!n) return;
            counts.set(n, (counts.get(n) || 0) + 1);
          });
        }
      } catch (e) {
        // ignore malformed shapes
      }
    });
    return counts;
  }, [filteredBase]);

  // Set of booth numbers that appear more than once
  const duplicateBoothNumbers = useMemo(() => {
    const arr = [];
    boothCounts.forEach((v, k) => { if (v > 1) arr.push(k); });
    return arr;
  }, [boothCounts]);

  // Final filtered list: if onlyDuplicates is set, show only transactions that include any duplicated booth
  const filtered = useMemo(() => {
    if (!onlyDuplicates) return visibleBase;
    const dupSet = new Set(duplicateBoothNumbers);
    return (visibleBase || []).filter(txn => {
      try {
        if (Array.isArray(txn.booths) && txn.booths.length > 0) {
          return txn.booths.some(b => dupSet.has(String(b.number || b.id || b.boothNumber || b).trim()));
        }
        if (Array.isArray(txn.metadata?.booths) && txn.metadata.booths.length > 0) {
          return txn.metadata.booths.some(b => dupSet.has(String(b.number || b.id || b.boothNumber || b).trim()));
        }
        if (txn.metadata?.boothNumber && dupSet.has(String(txn.metadata.boothNumber).trim())) return true;
        if (txn.metadata?.boothNumbers) {
          const raw = String(txn.metadata.boothNumbers);
          return raw.split(/[;,\s]+/).some(part => dupSet.has(part.trim()));
        }
      } catch (e) {
        return false;
      }
      return false;
    });
  }, [visibleBase, onlyDuplicates, duplicateBoothNumbers]);

  // Paginate filtered transactions
  // Sort then paginate
  const sortedTransactions = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const copy = Array.isArray(filtered) ? filtered.slice() : [];
    copy.sort((a, b) => {
      try {
        if (sortBy === 'date') {
          const toTs = (v) => {
            if (!v) return 0;
            if (typeof v.toMillis === 'function') return v.toMillis();
            if (v.seconds) return v.seconds * 1000;
            const d = new Date(v);
            return isNaN(d.getTime()) ? 0 : d.getTime();
          };
          return (toTs(a.createdAt) - toTs(b.createdAt)) * dir;
        }
        if (sortBy === 'status') {
          const sa = (a.status || '').toLowerCase();
          const sb = (b.status || '').toLowerCase();
          return sa === sb ? 0 : (sa > sb ? dir : -dir);
        }
        if (sortBy === 'vendor') {
          const va = (a.metadata?.vendorName || a.vendorNameFallback || '').toLowerCase();
          const vb = (b.metadata?.vendorName || b.vendorNameFallback || '').toLowerCase();
          return va === vb ? 0 : (va > vb ? dir : -dir);
        }
        if (sortBy === 'contact') {
          const ca = (a.contactName || a.metadata?.payeeName || '').toLowerCase();
          const cb = (b.contactName || b.metadata?.payeeName || '').toLowerCase();
          return ca === cb ? 0 : (ca > cb ? dir : -dir);
        }
        if (sortBy === 'booths') {
          const count = (t) => {
            if (Array.isArray(t.booths) && t.booths.length > 0) return t.booths.length;
            const possible = Number(t.metadata?.boothCount || t.metadata?.booth_count || 0);
            if (!isNaN(possible) && possible > 0) return possible;
            const att = Number(t.metadata?.attendeeCount || t.metadata?.attendee_count || t.metadata?.attendees || 0);
            return isNaN(att) ? 0 : att;
          };
          return (count(a) - count(b)) * dir;
        }
      } catch (e) {
        return 0;
      }
      return 0;
    });
    return copy;
  }, [filtered, sortBy, sortDir]);

  const paginatedTransactions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedTransactions.slice(startIndex, endIndex);
  }, [sortedTransactions, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchText, eventId, vendorNumber, onlyDuplicates, hideFailedTransactions, hideIncompleteTransactions]);
  // Reset to page 1 when purchase filter changes
  useEffect(() => { setCurrentPage(1); }, [purchaseFilter]);

  // Format timestamp
  const formatTimestamp = (ts) => {
    try {
      if (!ts) return '';
      if (typeof ts.toMillis === 'function') return new Date(ts.toMillis()).toLocaleString();
      if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleString();
      const d = new Date(ts);
      return isNaN(d.getTime()) ? '' : d.toLocaleString();
    } catch {
      return '';
    }
  };

  // Format currency
  const formatCurrency = (amountInCents) => {
    return `$${((amountInCents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'succeeded') return '#10b981';
    if (s === 'failed') return '#ef4444';
    if (s === 'incomplete') return '#f59e0b';
    if (s === 'refunded') return '#9333ea';
    if (s === 'partially_refunded') return '#d946ef';
    return '#6b7280';
  };

  // Reset filters
  const resetFilters = () => {
    setEventId('');
    setEventName('');
    setVendorId('All');
    setVendorName('All Vendors');
    setVendorNumber('All');
    setStatusFilter('All');
    setPurchaseFilter('All');
    setSearchText('');
    setHideFailedTransactions(false);
    setHideIncompleteTransactions(false);
  };

  // Refresh transactions without resetting filters
  const handleRefresh = () => {
    setLoading(true);
    setTransactions([]);
    // Increment trigger to force useEffect to re-run
    setRefreshTrigger(prev => prev + 1);
  };

  // Calculate totals
  const summary = useMemo(() => {
    const succeeded = filtered.filter(t => (t.status || '').toLowerCase() === 'succeeded' || (t.status || '').toLowerCase() === 'partially_refunded');
    const failed = filtered.filter(t => (t.status || '').toLowerCase() === 'failed');
    const incomplete = filtered.filter(t => (t.status || '').toLowerCase() === 'incomplete');
    const financialRows = filtered;
    
    // Calculate total original amount, total refunded, cancellation fees, and balance
    const totalOriginal = financialRows.reduce((sum, t) => sum + (t.amount || 0), 0);
    const totalRefunded = financialRows.reduce((sum, t) => sum + (t.totalRefunded || 0), 0);
    
    // Calculate total cancellation fees
    const totalCancellationFees = financialRows.reduce((sum, t) => {
      let fees = 0;
      if (t.refundHistory && Array.isArray(t.refundHistory)) {
        t.refundHistory.forEach(refund => {
          if (refund.status === 'succeeded' && refund.refundFee) {
            fees += refund.refundFee;
          }
        });
      }
      return sum + fees;
    }, 0);
    
    // totalRefunded already reflects net refunded Stripe amounts,
    // so cancellation fees should not be subtracted again.
    const totalBalance = totalOriginal - totalRefunded;

    return {
      totalCount: filtered.length,
      succeededCount: succeeded.length,
      failedCount: failed.length,
      incompleteCount: incomplete.length,
      totalOriginal,
      totalRefunded,
      totalCancellationFees,
      totalBalance,
    };
  }, [filtered]);

  // Total number of booths across the currently filtered transactions
  const totalBooths = useMemo(() => {
    return filtered.reduce((sum, t) => {
      if (Array.isArray(t.booths)) return sum + t.booths.length;
      const possible = Number(t.metadata?.boothCount || t.metadata?.booth_count || 0);
      return sum + (isNaN(possible) ? 0 : possible);
    }, 0);
  }, [filtered]);

  // Total number of attendees across the currently filtered transactions
  const totalAttendees = useMemo(() => {
    return filtered.reduce((sum, t) => {
      const possible = Number(t.metadata?.attendeeCount || t.metadata?.attendee_count || t.metadata?.attendees || 0);
      return sum + (isNaN(possible) ? 0 : possible);
    }, 0);
  }, [filtered]);

  const busyMode = useMemo(() => {
    if (loading) return 'transactions';
    if (eventsLoading) return 'events';
    if (vendorsLoading) return 'vendors';
    return '';
  }, [loading, eventsLoading, vendorsLoading]);

  const busyLabel = useMemo(() => {
    if (busyMode === 'transactions') return 'Loading transactions...';
    if (busyMode === 'events') return 'Loading events...';
    if (busyMode === 'vendors') return 'Loading vendors...';
    return '';
  }, [busyMode]);

  return (
    <div className="profile-container profile-wide-container">
      {busyMode && (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1500,
            cursor: 'wait',
            background: 'rgba(15, 23, 42, 0.22)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 160,
                height: 160,
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.55)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 28px rgba(0,0,0,0.45)',
                animation: 'transactionsBusyPulse 1.6s ease-in-out infinite',
              }}
            >
              <div
                style={{
                  width: 96,
                  height: 96,
                  borderRadius: '50%',
                  border: '6px solid rgba(255,255,255,0.85)',
                  borderTopColor: '#4ECDC4',
                  borderRightColor: '#ffb703',
                  borderBottomColor: '#9b5de5',
                  animation: 'transactionsBusySpin 1s linear infinite',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{busyLabel}</div>
          </div>
        </div>
      )}
      <style>{'@keyframes transactionsBusySpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } @keyframes transactionsBusyPulse { 0% { transform: scale(1); opacity: 0.95; } 50% { transform: scale(1.06); opacity: 1; } 100% { transform: scale(1); opacity: 0.95; } }'}</style>
      <div className="profile-header-row">
        <button type="button" className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        <div style={{ flex: 1 }} />
        {(isAdmin || !!eventId || statusFilter !== 'All') && (
          <button 
            type="button" 
            className="back-btn" 
            onClick={resetFilters}
            style={{ marginRight: 8, backgroundColor: '#6b7280', color: '#fff' }}
          >
            Reset Filters
          </button>
        )}
        <button 
          type="button" 
          className="back-btn" 
          onClick={handleRefresh}
          style={{ marginRight: 8, backgroundColor: '#28a745', color: '#fff' }}
        >
          🔄 Refresh
        </button>
        <button type="button" className="cancel-btn" onClick={() => navigate('/')}>Close</button>
      </div>
      <h2 className="profile-title">{isAdmin ? 'Admin: Transactions' : 'My Transactions'}</h2>
      <div style={{ marginBottom: 12, fontSize: 14, color: '#374151' }}>
        <strong>Total booths:</strong> {totalBooths}
      </div>

      {/* Active filters indicator */}
      {isAdmin && (searchText || !!eventId || vendorId !== 'All' || statusFilter !== 'All' || onlyDuplicates) && (
        <div style={{ 
          marginBottom: 12, 
          padding: '8px 12px', 
          backgroundColor: '#eff6ff', 
          borderRadius: '4px',
          fontSize: '14px',
          color: '#1e40af'
        }}>
          <strong>Active filters:</strong>{' '}
          {searchText && <span>Search: "{searchText}" • </span>}
          {eventId && <span>Event: {eventName} • </span>}
          {vendorId !== 'All' && <span>Vendor: {vendorName} • </span>}
          {statusFilter !== 'All' && <span>Status: {statusFilter}</span>}
          {onlyDuplicates && <span> • Only Duplicates</span>}
        </div>
      )}

      {/* Filters - only show for admin users */}
      {isAdmin && (
        <div style={{ marginBottom: 12 }}>
          <h4 style={{ margin: '0 0 8px 0' }}>Filters</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px' }}>
            <div>
              <label className="profile-label">Event</label>
              <select
                className="profile-input"
                value={eventId}
                onChange={(e) => {
                  const val = e.target.value;
                  const opt = eventChoices.find(p => p.eventId === val);
                  setEventId(val);
                  setEventName(opt ? opt.eventName : val);
                }}
                disabled={eventsLoading}
              >
                <option value="">Select an event…</option>
                {eventsLoading && <option value="" disabled>Loading events…</option>}
                {!eventsLoading && eventChoices.length === 0 && <option value="" disabled>No events</option>}
                {!eventsLoading && eventChoices.length > 0 && eventChoices.map(p => (
                  <option key={p.eventId} value={p.eventId}>{p.eventName}</option>
                ))}
              </select>
              {eventId && <div className="profile-hint">Event ID: {eventId}</div>}
            </div>
            <div>
              <label className="profile-label">Vendor</label>
              <select
                className="profile-input"
                value={vendorId}
                onChange={(e) => {
                  const val = e.target.value;
                  const opt = vendorChoices.find(p => p.vendorId === val);
                  setVendorId(val);
                  setVendorName(opt ? opt.vendorName : (val === 'All' ? 'All Vendors' : val));
                  setVendorNumber(opt ? opt.vendorNumber : 'All');
                }}
                disabled={vendorsLoading}
              >
                <option value="All">All Vendors</option>
                {vendorsLoading && <option value="" disabled>Loading vendors…</option>}
                {!vendorsLoading && vendorChoices.length > 0 && vendorChoices.map(p => (
                  <option key={p.vendorId} value={p.vendorId}>{p.vendorName}</option>
                ))}
              </select>
              {vendorId && vendorId !== 'All' && (
                <div className="profile-hint">
                  Vendor ID: {vendorId} | Vendor Number: {vendorNumber}
                </div>
              )}
            </div>
            <div>
              <label className="profile-label">Vendor/Contact/Email/Booth</label>
              <input
                type="text"
                className="profile-input"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {searchText && (
                <div className="profile-hint" style={{ color: '#059669' }}>
                  Searching in vendor name, contact name, email, and booth
                </div>
              )}
            </div>
            <div>
              <label className="profile-label">Status</label>
              <select
                className="profile-input"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="All">All</option>
                <option value="Succeeded">Succeeded</option>
                <option value="Refunded">Refunded</option>
                <option value="Partially Refunded">Partially Refunded</option>
                <option value="Failed">Failed</option>
                <option value="Incomplete">Incomplete</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="profile-label">Purchase Type</label>
              <select
                className="profile-input"
                value={purchaseFilter}
                onChange={(e) => setPurchaseFilter(e.target.value)}
              >
                <option value="All">All</option>
                <option value="Booths">Booths</option>
                <option value="Attendees">Attendees</option>
                <option value="Manual">Manual</option>
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={onlyDuplicates} onChange={(e) => setOnlyDuplicates(!!e.target.checked)} />
                <span style={{ fontSize: 13, color: '#374151' }}>Show only transactions with duplicated booth numbers</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={hideFailedTransactions}
                  onChange={(e) => setHideFailedTransactions(!!e.target.checked)}
                />
                <span style={{ fontSize: 13, color: '#374151' }}>Hide failed transactions</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={hideIncompleteTransactions}
                  onChange={(e) => setHideIncompleteTransactions(!!e.target.checked)}
                />
                <span style={{ fontSize: 13, color: '#374151' }}>Hide incomplete transactions</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Status Filter for non-admin users */}
      {!isAdmin && (
        <div style={{ marginBottom: 12 }}>
          <h4 style={{ margin: '0 0 8px 0' }}>Filters</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', maxWidth: '800px' }}>
            <div>
              <label className="profile-label">Event</label>
              <select
                className="profile-input"
                value={eventId}
                onChange={(e) => {
                  const val = e.target.value;
                  const opt = eventChoices.find(p => p.eventId === val);
                  setEventId(val);
                  setEventName(opt ? opt.eventName : val);
                }}
                disabled={eventsLoading}
              >
                <option value="">Select an event…</option>
                {eventsLoading && <option value="" disabled>Loading events…</option>}
                {!eventsLoading && eventChoices.length === 0 && <option value="" disabled>No events</option>}
                {!eventsLoading && eventChoices.length > 0 && eventChoices.map(p => (
                  <option key={p.eventId} value={p.eventId}>{p.eventName}</option>
                ))}
              </select>
              {eventId && <div className="profile-hint">Event ID: {eventId}</div>}
            </div>
            <div>
              <label className="profile-label">Status</label>
              <select
                className="profile-input"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="All">All</option>
                <option value="Succeeded">Succeeded</option>
                <option value="Refunded">Refunded</option>
                <option value="Partially Refunded">Partially Refunded</option>
                <option value="Failed">Failed</option>
                <option value="Incomplete">Incomplete</option>
                <option value="Other">Other</option>
              </select>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={hideFailedTransactions}
                      onChange={(e) => setHideFailedTransactions(!!e.target.checked)}
                    />
                    <span style={{ fontSize: 13, color: '#374151' }}>Hide failed transactions</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={hideIncompleteTransactions}
                      onChange={(e) => setHideIncompleteTransactions(!!e.target.checked)}
                    />
                    <span style={{ fontSize: 13, color: '#374151' }}>Hide incomplete transactions</span>
                  </label>
                </div>
            </div>
            <div>
              <label className="profile-label">Purchase Type</label>
              <select
                className="profile-input"
                value={purchaseFilter}
                onChange={(e) => setPurchaseFilter(e.target.value)}
              >
                <option value="All">All</option>
                <option value="Booths">Booths</option>
                <option value="Attendees">Attendees</option>
                <option value="Manual">Manual</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {/* Summary Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: 12 }}>
        <div style={{ padding: '12px', background: '#f3f4f6', borderRadius: '6px', minWidth: 0 }}>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '3px' }}>Total Transactions</div>
          <div style={{ fontSize: 'clamp(16px, 2vw, 20px)', fontWeight: 700, lineHeight: 1.2, wordBreak: 'break-word' }}>{summary.totalCount}</div>
        </div>
        <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '6px', minWidth: 0 }}>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '3px' }}>Total Booths</div>
          <div style={{ fontSize: 'clamp(16px, 2vw, 20px)', fontWeight: 700, lineHeight: 1.2, wordBreak: 'break-word' }}>{totalBooths}</div>
        </div>
        <div style={{ padding: '12px', background: '#fff8f0', borderRadius: '6px', minWidth: 0 }}>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '3px' }}>Total Attendees</div>
          <div style={{ fontSize: 'clamp(16px, 2vw, 20px)', fontWeight: 700, lineHeight: 1.2, wordBreak: 'break-word' }}>{totalAttendees}</div>
        </div>
        <div style={{ padding: '12px', background: '#ecfdf5', borderRadius: '6px', minWidth: 0 }}>
          <div style={{ fontSize: '12px', color: '#059669', marginBottom: '3px' }}>Succeeded</div>
          <div style={{ fontSize: 'clamp(16px, 2vw, 20px)', fontWeight: 700, color: '#10b981', lineHeight: 1.2, wordBreak: 'break-word' }}>{summary.succeededCount}</div>
        </div>
        <div style={{ padding: '12px', background: '#fef3c7', borderRadius: '6px', minWidth: 0 }}>
          <div style={{ fontSize: '12px', color: '#d97706', marginBottom: '3px' }}>Incomplete</div>
          <div style={{ fontSize: 'clamp(16px, 2vw, 20px)', fontWeight: 700, color: '#f59e0b', lineHeight: 1.2, wordBreak: 'break-word' }}>{summary.incompleteCount}</div>
        </div>
        <div style={{ padding: '12px', background: '#fef2f2', borderRadius: '6px', minWidth: 0 }}>
          <div style={{ fontSize: '12px', color: '#dc2626', marginBottom: '3px' }}>Failed</div>
          <div style={{ fontSize: 'clamp(16px, 2vw, 20px)', fontWeight: 700, color: '#ef4444', lineHeight: 1.2, wordBreak: 'break-word' }}>{summary.failedCount}</div>
        </div>
        <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '6px', minWidth: 0 }}>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '3px' }}>Total Original</div>
          <div style={{ fontSize: 'clamp(15px, 1.8vw, 19px)', fontWeight: 700, color: '#111', lineHeight: 1.2, wordBreak: 'break-word' }}>{formatCurrency(summary.totalOriginal)}</div>
        </div>
        <div style={{ padding: '12px', background: '#ecfdf5', borderRadius: '6px', minWidth: 0 }}>
          <div style={{ fontSize: '12px', color: '#059669', marginBottom: '3px' }}>Total Refunded</div>
          <div style={{ fontSize: 'clamp(15px, 1.8vw, 19px)', fontWeight: 700, color: '#10b981', lineHeight: 1.2, wordBreak: 'break-word' }}>{formatCurrency(summary.totalRefunded)}</div>
        </div>
        <div style={{ padding: '12px', background: '#fef2f2', borderRadius: '6px', minWidth: 0 }}>
          <div style={{ fontSize: '12px', color: '#dc2626', marginBottom: '3px' }}>Cancellation Fees</div>
          <div style={{ fontSize: 'clamp(15px, 1.8vw, 19px)', fontWeight: 700, color: '#ef4444', lineHeight: 1.2, wordBreak: 'break-word' }}>{formatCurrency(summary.totalCancellationFees)}</div>
        </div>
        <div style={{ padding: '12px', background: '#faf5ff', borderRadius: '6px', minWidth: 0 }}>
          <div style={{ fontSize: '12px', color: '#7c3aed', marginBottom: '3px' }}>Total Balance</div>
          <div style={{ fontSize: 'clamp(15px, 1.8vw, 19px)', fontWeight: 700, color: '#9333ea', lineHeight: 1.2, wordBreak: 'break-word' }}>{formatCurrency(summary.totalBalance)}</div>
        </div>
      </div>

      {error && <div className="profile-error" style={{ marginBottom: 8 }}>{error}</div>}

      {/* Transactions Table */}
      <div style={{ overflowX: 'auto' }}>
        {loading ? (
          <div>Loading…</div>
        ) : (
          <table className="profile-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', lineHeight: 1.25 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px' }}>
                  <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('date'); setSortDir(sortBy === 'date' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                    Date {sortBy === 'date' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </span>
                </th>
                <th style={{ textAlign: 'left', padding: '6px' }}>
                  <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('status'); setSortDir(sortBy === 'status' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                    Status {sortBy === 'status' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </span>
                </th>
                <th style={{ textAlign: 'left', padding: '6px' }}>
                  <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('vendor'); setSortDir(sortBy === 'vendor' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                    Vendor {sortBy === 'vendor' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </span>
                </th>
                <th style={{ textAlign: 'left', padding: '6px' }}>
                  <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('contact'); setSortDir(sortBy === 'contact' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                    Contact {sortBy === 'contact' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </span>
                </th>
                <th style={{ textAlign: 'left', padding: '6px' }}>
                  <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => { setSortBy('booths'); setSortDir(sortBy === 'booths' ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc'); }}>
                    Booths/Attendees {sortBy === 'booths' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                  </span>
                </th>
                <th style={{ textAlign: 'right', padding: '6px' }}>Original Amount</th>
                <th style={{ textAlign: 'right', padding: '6px' }}>Total Refunded</th>
                <th style={{ textAlign: 'right', padding: '6px' }}>Cancellation Fees</th>
                <th style={{ textAlign: 'right', padding: '6px' }}>Balance</th>
                <th style={{ textAlign: 'left', padding: '6px' }}>Failure Reason</th>
                <th style={{ textAlign: 'center', padding: '6px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTransactions.length === 0 ? (
                <tr><td colSpan={isAdmin ? 11 : 10} style={{ padding: 12, textAlign: 'center', color: '#666' }}>No transactions found</td></tr>
              ) : (
                paginatedTransactions.map(txn => {
                  const totalRefunded = txn.totalRefunded || 0;
                  
                  // Calculate cancellation fees from refund history
                  let cancellationFees = 0;
                  if (txn.refundHistory && Array.isArray(txn.refundHistory)) {
                    txn.refundHistory.forEach(refund => {
                      if (refund.status === 'succeeded') {
                        if (refund.refundFee) {
                          cancellationFees += refund.refundFee;
                        }
                      }
                    });
                  }
                  
                  // totalRefunded already includes all successful refund amounts.
                  const balance = txn.amount - totalRefunded;
                  
                  return (
                  <tr key={txn.id}>
                    <td style={{ padding: '6px', borderTop: '1px solid #eee' }}>
                      {formatTimestamp(txn.createdAt)}
                    </td>
                    <td style={{ padding: '6px', borderTop: '1px solid #eee' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 600,
                        color: '#fff',
                        backgroundColor: getStatusColor(txn.status),
                      }}>
                        {(txn.status || 'unknown').toUpperCase()}
                      </span>
                      {txn.attemptCount > 0 && (
                        <div style={{ 
                          fontSize: '11px', 
                          color: txn.status === 'succeeded' ? '#059669' : '#ef4444', 
                          marginTop: 4,
                          fontWeight: 500
                        }}>
                          {txn.status === 'succeeded' 
                            ? `✓ After ${txn.attemptCount} ${txn.attemptCount === 1 ? 'attempt' : 'attempts'}`
                            : `${txn.attemptCount} ${txn.attemptCount === 1 ? 'attempt' : 'attempts'}`
                          }
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '6px', borderTop: '1px solid #eee' }}>
                      {txn.metadata?.vendorName || txn.vendorNameFallback || '—'}
                    </td>
                    <td style={{ padding: '6px', borderTop: '1px solid #eee' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600 }}>{txn.contactName || txn.metadata?.payeeName || '—'}</div>
                      {txn.contactEmail && <div style={{ fontSize: '12px', color: '#6b7280' }}>{txn.contactEmail}</div>}
                    </td>
                    <td style={{ padding: '6px', borderTop: '1px solid #eee' }}>
                      {Array.isArray(txn.booths) && txn.booths.length > 0
                        ? txn.booths.map(b => b.number || b.id || '—').join(', ')
                        : txn.metadata?.purchaseType === 'attendees'
                          ? `Attendees: ${txn.metadata?.attendeeCount || '—'}`
                          : (txn.metadata?.boothCount ? `${txn.metadata.boothCount}` : '—')}
                    </td>
                    <td style={{ padding: '6px', borderTop: '1px solid #eee', textAlign: 'right', fontWeight: 600, color: '#111' }}>
                      {formatCurrency(txn.amount)}
                    </td>
                    <td style={{ padding: '6px', borderTop: '1px solid #eee', textAlign: 'right', fontWeight: 600, color: totalRefunded > 0 ? '#10b981' : '#6b7280' }}>
                      {totalRefunded > 0 ? formatCurrency(totalRefunded) : '—'}
                    </td>
                    <td style={{ padding: '6px', borderTop: '1px solid #eee', textAlign: 'right', fontWeight: 600, color: cancellationFees > 0 ? '#ef4444' : '#6b7280' }}>
                      {cancellationFees > 0 ? formatCurrency(cancellationFees) : '—'}
                    </td>
                    <td style={{ padding: '6px', borderTop: '1px solid #eee', textAlign: 'right', fontWeight: 600, color: '#9333ea' }}>
                      {formatCurrency(balance)}
                    </td>
                    <td style={{ padding: '6px', borderTop: '1px solid #eee', color: '#ef4444', fontSize: '12px' }}>
                      {txn.failureReason || '—'}
                    </td>
                    <td style={{ padding: '8px', borderTop: '1px solid #eee', textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => navigate(`/transactions/${txn.paymentIntentId || txn.id}`)}
                        style={{
                          padding: '3px 8px',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#fff',
                          backgroundColor: '#3b82f6',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}

        {/* Pagination Controls */}
        {!loading && filtered.length > 0 && (
          <div style={{ 
            marginTop: 24,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 0'
          }}>
            <div style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>
              Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filtered.length)} of {filtered.length} transactions
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: currentPage === 1 ? '#f3f4f6' : 'white',
                  color: currentPage === 1 ? '#9ca3af' : '#374151',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                  fontSize: '14px'
                }}
              >
                Previous
              </button>
              <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: currentPage === totalPages ? '#f3f4f6' : 'white',
                  color: currentPage === totalPages ? '#9ca3af' : '#374151',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                  fontSize: '14px'
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionsAdmin;
